import type { ReactElement } from "react"
import React from "react"
import type { ProductBundle as ProductBundleType } from "lib/onestore-graphql/Model"
import { get } from "lodash"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import type { MarketplaceProduct } from "@gatsby-plugin-marketplace/fragments/marketplaceProduct"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import useAnchorProperties from "~/hooks/useAnchorProperties"
import useProductSettings from "~/hooks/useProductSettings"
import useSaleConfigurationState from "~/hooks/useSaleConfigurationState"
import isEmpty from "~/lib/isEmpty"
import { getProductPricing } from "~/lib/pricing"
import { COMPONENT_ID } from "~/types/component"
import { prepareComponentValues } from "~/utils"
import ProductBundleItem from "./ProductBundleItem"

type ProductBundleProps = {
  bundles: ProductBundleType[]
  product: MarketplaceProduct
}

export default function ProductBundle({
  bundles,
  product,
}: ProductBundleProps): ReactElement<ProductBundleProps> | null {
  const { currentPriceType } = usePriceTypeContext()

  const saleConfiguration = product.flatData.saleConfigurations[0]

  const productItemAnchor = useAnchorProperties(COMPONENT_ID.PRODUCT_HERO)
  const productSettings = useProductSettings(saleConfiguration)

  const { options } = useSaleConfigurationState(saleConfiguration.id)

  const defaultPlan: CloudBluePlan | undefined = get(
    saleConfiguration,
    "flatData.defaultCloudBluePlan[0]"
  )

  if (isEmpty(defaultPlan)) {
    return null
  }

  const componentValues = prepareComponentValues(
    saleConfiguration.flatData.optionsMapping || [],
    options,
    defaultPlan
  )

  const plan = defaultPlan.flatData

  if (!componentValues || !plan) {
    return null
  }

  const { priceValues } = componentValues

  const currentPrice = getProductPricing(priceValues, currentPriceType)

  const productSettingsPlanName: string | undefined = get(
    productSettings,
    "cloudBluePlan[0].flatData.name"
  )

  const productItem = {
    title: !isEmpty(productSettingsPlanName)
      ? productSettingsPlanName
      : plan.name,
    price: `${currentPrice.priceDecimals},${currentPrice.priceCents} ${currentPrice.currency}`,
    priceSuffix: `${currentPrice.priceSubtext}`,
    img:
      product.flatData.logoBundle && product.flatData.logoBundle.length
        ? {
            src: product.flatData.logoBundle[0].url,
            alt: plan.name,
            image: plan.name,
          }
        : undefined,
    href: productItemAnchor.href,
    hrefTitle: plan.name,
    onClick: productItemAnchor.onClick,
  }

  return (
    <>
      {bundles.map((bundle) =>
        !isEmpty(productSettings) ? (
          <ProductBundleItem
            key={bundle.id}
            productItem={productItem}
            bundle={bundle}
            productSettings={productSettings}
            upsell={saleConfiguration.flatData.upsell}
          />
        ) : null
      )}
    </>
  )
}
